import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import styled from '@emotion/styled';
import { Button, CircularProgress, Alert } from '@mui/material';
import { H3, Body1, Body2 } from '../../../../layouts/TextStyles';
import { tone, ui } from '../../../../utils/uiTokens'

const PaymentStatusPage = () => {
    const { formatMessage } = useIntl();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'pending'>('loading');
    const [message, setMessage] = useState('');

    const invoiceId = searchParams.get('invoiceId');
    const statusParam = searchParams.get('status');
    const errorCode = searchParams.get('errorCode');
    const errorMessage = searchParams.get('errorMessage');

    useEffect(() => {
        // Определяем статус платежа на основе параметров URL
        if (statusParam === 'success' || statusParam === 'completed') {
            setStatus('success');
            setMessage(formatMessage({ id: 'payment_success_message' }));
        } else if (statusParam === 'error' || statusParam === 'failed' || errorCode) {
            setStatus('error');
            setMessage(errorMessage || formatMessage({ id: 'payment_error_message' }));
        } else if (statusParam === 'pending') {
            setStatus('pending');
            setMessage(formatMessage({ id: 'payment_pending_message' }));
        } else {
            setStatus('error');
            setMessage(formatMessage({ id: 'payment_unknown_status' }));
        }
    }, [statusParam, errorCode, errorMessage, formatMessage]);

    const handleGoToCourses = () => {
        navigate('/sell-course');
    };

    const handleRetryPayment = () => {
        navigate('/sell-course');
    };

    return (
        <Wrapper>
            <Content>
                <StatusContainer>
                    {status === 'loading' && (
                        <>
                            <CircularProgress size={60} />
                            <H3>{formatMessage({ id: 'checking_payment_status' })}</H3>
                            <Body2>{formatMessage({ id: 'please_wait' })}</Body2>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <SuccessIcon>✅</SuccessIcon>
                            <H3 color={tone.ok.text}>{formatMessage({ id: 'payment_success_title' })}</H3>
                            <Body1>{message}</Body1>
                            <Body2 color={ui.sub}>
                                {formatMessage({ id: 'payment_success_details' })}
                            </Body2>
                            <ButtonContainer>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleGoToCourses}
                                    sx={{ mr: 2 }}
                                >
                                    {formatMessage({ id: 'go_to_courses' })}
                                </Button>
                            </ButtonContainer>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <ErrorIcon>❌</ErrorIcon>
                            <H3 color={tone.bad.text}>{formatMessage({ id: 'payment_error_title' })}</H3>
                            <Body1>{message}</Body1>
                            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                                {formatMessage({ id: 'payment_error_help' })}
                            </Alert>
                            <ButtonContainer>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleRetryPayment}
                                    sx={{ mr: 2 }}
                                >
                                    {formatMessage({ id: 'try_again' })}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleGoToCourses}
                                >
                                    {formatMessage({ id: 'back_to_courses' })}
                                </Button>
                            </ButtonContainer>
                        </>
                    )}

                    {status === 'pending' && (
                        <>
                            <PendingIcon>⏳</PendingIcon>
                            <H3 color={tone.warn.text}>{formatMessage({ id: 'payment_pending_title' })}</H3>
                            <Body1>{message}</Body1>
                            <Body2 color={ui.sub}>
                                {formatMessage({ id: 'payment_pending_details' })}
                            </Body2>
                            <ButtonContainer>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleGoToCourses}
                                >
                                    {formatMessage({ id: 'back_to_courses' })}
                                </Button>
                            </ButtonContainer>
                        </>
                    )}

                    {invoiceId && (
                        <PaymentDetails>
                            <Body2 color={ui.muted}>
                                {formatMessage({ id: 'invoice_id' })}: {invoiceId}
                            </Body2>
                        </PaymentDetails>
                    )}
                </StatusContainer>
            </Content>
        </Wrapper>
    );
};

export default PaymentStatusPage;

// Styled Components
const Wrapper = styled.div`
    min-height: 100vh;
    background-color: ${ui.surface};
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
`;

const Content = styled.div`
    background-color: ${ui.card};
    border-radius: 16px;
    padding: 40px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    max-width: 500px;
    width: 100%;
    text-align: center;
`;

const StatusContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
`;

const SuccessIcon = styled.div`
    font-size: 80px;
    margin-bottom: 16px;
`;

const ErrorIcon = styled.div`
    font-size: 80px;
    margin-bottom: 16px;
`;

const PendingIcon = styled.div`
    font-size: 80px;
    margin-bottom: 16px;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 12px;
    margin-top: 24px;
    justify-content: center;
    flex-wrap: wrap;
`;

const PaymentDetails = styled.div`
    margin-top: 24px;
    padding: 16px;
    background-color: ${ui.surface};
    border-radius: 8px;
    border: 1px solid ${ui.border};
`;